<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import resolveUrl from '@/services/resolveUrl';

// Primeira URL http(s) do texto — usada pra montar a prévia ao vivo (WhatsApp Web).
const URL_RE = /\bhttps?:\/\/[^\s]+/i;

const EMOJIS = [
  '😀', '😁', '😂', '🤣', '😅', '😊', '😇', '🙂', '😉', '😍', '😘', '🥰', '😎', '🤩', '🥳',
  '🤔', '🤨', '😐', '😶', '🙄', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '😢', '😭', '😤', '😠', '😡',
  '👍', '👎', '👏', '🙌', '🤝', '👋', '🙏', '💪', '👌', '✌️', '🤞', '✊',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💔', '💕', '✨', '🔥', '⭐', '🎉', '🎊', '🎁',
  '✅', '❌', '⚠️', '❗', '❓', '💯', '🆗', '🆘',
];

export default {
  name: 'MensagemComposer',
  props: {
    disabled: { type: Boolean, default: false },
    conversaId: { type: [Number, String], default: null },
  },
  emits: ['enviar'],
  data() {
    return {
      texto: '',
      enviando: false,
      // Anexos
      arquivoSelecionado: null,
      previewArquivo: null,
      tipoSelecionado: null, // 'imagem' | 'video' | 'documento'
      caption: '',
      // Áudio
      gravando: false,
      pausado: false,
      audioBlob: null,
      audioUrl: null,
      // URL temporária do que foi gravado até a pausa — permite ouvir
      // antes de retomar/enviar. Limpada ao retomar ou descartar.
      previewUrl: null,
      mediaRecorder: null,
      tempoGravacao: 0,
      timerGravacao: null,
      // Quando true, dispara o envio assim que o blob é gerado pelo onstop
      // (usado pra "clicar em enviar enquanto gravando" finalizar+enviar direto).
      enviarAposStop: false,
      // Emoji
      mostrarEmoji: false,
      emojis: EMOJIS,
      // Menu "+" (anexos / formatação)
      mostrarMenuMais: false,
      // Localização
      enviandoLocalizacao: false,
      // Prévia de link ao vivo (composer), igual ao WhatsApp Web.
      linkPreview: null, // { url, title, description, siteName, image }
      linkPreviewLoading: false,
      linkPreviewUrl: null, // URL que está previewada/em busca
      linkPreviewDismissedUrl: null, // URL que o usuário fechou (não re-busca)
    };
  },
  computed: {
    ...mapGetters('atendimento', ['respondendoA']),
    podeEnviarTexto() { return Boolean(this.texto.trim()) && !this.enviando && !this.disabled; },
    // Igual WhatsApp Web: sem texto e sem gravação ativa → mostra microfone
    // (clique inicia gravação). Com texto → mostra botão de enviar.
    mostrarBotaoMicrofone() {
      return !this.texto.trim() && !this.gravando && !this.audioBlob;
    },
    // Estado de gravação ativa (substitui o composer durante captura).
    emModoGravacao() { return this.gravando || Boolean(this.audioBlob); },
    duracaoFormatada() {
      const s = this.tempoGravacao;
      const min = Math.floor(s / 60);
      const seg = s % 60;
      return `${min}:${seg.toString().padStart(2, '0')}`;
    },
    previaResposta() {
      const m = this.respondendoA;
      if (!m) return null;
      if (m.conteudo) {
        let t = m.conteudo;
        if (t.length > 100) t = `${t.slice(0, 100)}…`;
        return t;
      }
      const labels = { imagem: '📷 Imagem', audio: '🎤 Áudio', video: '🎬 Vídeo', documento: '📄 Documento', localizacao: '📍 Localização', template: '📋 Template' };
      return labels[m.tipo] || '↩ Mensagem';
    },
    autorResposta() {
      const m = this.respondendoA;
      if (!m) return '';
      return m.direcao === 'entrada' ? 'Filiado' : 'Você';
    },
  },
  watch: {
    // No mobile não damos foco automático (evita abrir o teclado sozinho ao
    // entrar na conversa). No desktop mantém o foco pra digitar direto.
    disabled(novo) { if (!novo && !this._ehMobile()) this._focar(); },
    texto() { this._agendarPreviewLink(); },
  },
  mounted() {
    if (!this._ehMobile()) this._focar();
    document.addEventListener('click', this._fecharPopups);
  },
  beforeUnmount() {
    document.removeEventListener('click', this._fecharPopups);
    if (this._linkTimer) clearTimeout(this._linkTimer);
    this._limparGravacao();
  },
  methods: {
    ...mapActions('atendimento', ['setRespondendoA']),
    cancelarResposta() { this.setRespondendoA(null); },
    _ehMobile() {
      return Boolean(window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches);
    },
    _focar() {
      const el = this.$refs.textarea;
      if (el && !this.disabled) el.focus();
    },
    _fecharPopups(e) {
      if (this.mostrarEmoji && this.$refs.emojiBox && !this.$refs.emojiBox.contains(e.target)) {
        this.mostrarEmoji = false;
      }
      if (this.mostrarMenuMais && this.$refs.menuMaisBox && !this.$refs.menuMaisBox.contains(e.target)) {
        this.mostrarMenuMais = false;
      }
    },
    abrirAnexoArquivo() { this.mostrarMenuMais = false; this.$refs.inputArquivo.click(); },
    abrirAnexoImagem() { this.mostrarMenuMais = false; this.$refs.inputImagem.click(); },
    chamarLocalizacao() { this.mostrarMenuMais = false; this.enviarLocalizacao(); },
    aplicarFormatacao(prefixo, sufixo) { this.mostrarMenuMais = false; this.formatar(prefixo, sufixo); },

    // ── Prévia de link ao vivo (WhatsApp Web) ──────────────────────────
    resolveAssetUrl(relativeUrl) {
      if (!relativeUrl) return '';
      if (/^(https?:|data:)/i.test(relativeUrl)) return relativeUrl;
      const base = resolveUrl(process.env.VUE_APP_APIURL) || '';
      return `${base.replace(/\/$/, '')}${relativeUrl}`;
    },
    _urlDoTexto() {
      const m = String(this.texto || '').match(URL_RE);
      if (!m) return null;
      return m[0].replace(/[.,;:!?)\]]+$/, '');
    },
    _agendarPreviewLink() {
      const url = this._urlDoTexto();
      if (!url) { this._limparPreviewLink(); return; }
      if (url === this.linkPreviewDismissedUrl) return; // usuário fechou este
      if (url === this.linkPreviewUrl) return; // já temos/estamos buscando
      if (this._linkTimer) clearTimeout(this._linkTimer);
      this._linkTimer = setTimeout(() => this._buscarPreviewLink(url), 600);
    },
    async _buscarPreviewLink(url) {
      this.linkPreviewUrl = url;
      this.linkPreview = null;
      this.linkPreviewLoading = true;
      try {
        const { data } = await axios.get('/link/preview', { params: { url } });
        // Corrida: só aplica se ainda é a URL atual do texto e não foi fechada.
        if (this._urlDoTexto() !== url || url === this.linkPreviewDismissedUrl) return;
        this.linkPreview = data && (data.title || data.image) ? data : null;
      } catch (_e) {
        this.linkPreview = null;
      } finally {
        if (this.linkPreviewUrl === url) this.linkPreviewLoading = false;
      }
    },
    fecharPreviewLink() {
      this.linkPreviewDismissedUrl = this.linkPreviewUrl;
      this.linkPreview = null;
      this.linkPreviewLoading = false;
    },
    _limparPreviewLink() {
      if (this._linkTimer) { clearTimeout(this._linkTimer); this._linkTimer = null; }
      this.linkPreview = null;
      this.linkPreviewLoading = false;
      this.linkPreviewUrl = null;
      this.linkPreviewDismissedUrl = null;
    },

    // ── Texto ──────────────────────────────────────────────────────────
    async enviarTexto() {
      if (!this.podeEnviarTexto) return;
      this.enviando = true;
      try {
        await this.$emit('enviar', { conteudo: this.texto });
        this.texto = '';
        this._limparPreviewLink();
      } finally {
        this.enviando = false;
        this.$nextTick(() => this._focar());
      }
    },
    onKeyDown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.enviarTexto();
      }
    },

    // ── Formatação WhatsApp ────────────────────────────────────────────
    formatar(prefixo, sufixo) {
      const el = this.$refs.textarea;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const sel = this.texto.substring(start, end);
      const novo = `${this.texto.substring(0, start)}${prefixo}${sel || 'texto'}${sufixo}${this.texto.substring(end)}`;
      this.texto = novo;
      this.$nextTick(() => {
        el.focus();
        el.setSelectionRange(start + prefixo.length, end + prefixo.length);
      });
    },

    // ── Emoji ──────────────────────────────────────────────────────────
    inserirEmoji(emoji) {
      const el = this.$refs.textarea;
      const pos = el ? el.selectionStart : this.texto.length;
      this.texto = `${this.texto.substring(0, pos)}${emoji}${this.texto.substring(pos)}`;
      this.mostrarEmoji = false;
      this.$nextTick(() => {
        if (el) {
          el.focus();
          el.setSelectionRange(pos + emoji.length, pos + emoji.length);
        }
      });
    },

    // ── Anexar arquivo ─────────────────────────────────────────────────
    onArquivoEscolhido(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.arquivoSelecionado = file;
      // Detecta tipo
      if (file.type.startsWith('image/')) this.tipoSelecionado = 'imagem';
      else if (file.type.startsWith('video/')) this.tipoSelecionado = 'video';
      else if (file.type.startsWith('audio/')) this.tipoSelecionado = 'audio';
      else this.tipoSelecionado = 'documento';
      // Preview pra imagens
      if (this.tipoSelecionado === 'imagem' || this.tipoSelecionado === 'video') {
        this.previewArquivo = URL.createObjectURL(file);
      } else {
        this.previewArquivo = null;
      }
      this.caption = '';
      // Reset o input pra permitir reescolher o mesmo arquivo
      event.target.value = '';
    },
    cancelarArquivo() {
      if (this.previewArquivo) URL.revokeObjectURL(this.previewArquivo);
      this.arquivoSelecionado = null;
      this.previewArquivo = null;
      this.tipoSelecionado = null;
      this.caption = '';
    },
    async enviarArquivo() {
      if (!this.arquivoSelecionado || !this.conversaId) return;
      this.enviando = true;
      try {
        const form = new FormData();
        form.append('file', this.arquivoSelecionado);
        form.append('tipo', this.tipoSelecionado);
        if (this.caption) form.append('caption', this.caption);
        // NÃO setar Content-Type manualmente — axios precisa gerar com boundary
        await axios.post(`/conversa/${this.conversaId}/mensagem/midia`, form);
        this.cancelarArquivo();
      } catch (error) {
        const data = error.response && error.response.data;
        const msg = (data && (data.details || data.error)) || error.message;
        // eslint-disable-next-line no-alert
        alert(`Falha ao enviar arquivo: ${msg}`);
      } finally {
        this.enviando = false;
      }
    },

    // ── Áudio (MediaRecorder) ──────────────────────────────────────────
    async iniciarGravacao() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Meta WhatsApp aceita pra áudio: aac, amr, mpeg, mp4, ogg (opus).
        // NÃO aceita: webm.
        const candidatos = [
          'audio/ogg;codecs=opus',
          'audio/ogg',
          'audio/mp4;codecs=mp4a.40.2',
          'audio/mp4',
          'audio/mpeg',
        ];
        let mimeType = '';
        const suportados = [];
        for (const m of candidatos) {
          if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) {
            suportados.push(m);
            if (!mimeType) mimeType = m;
          }
        }
        // eslint-disable-next-line no-console
        console.log('[Audio] mimeTypes suportados pelo browser:', suportados, '→ usando:', mimeType || '(default do browser)');
        const mrOpts = mimeType ? { mimeType } : {};
        const mr = new MediaRecorder(stream, mrOpts);
        const chunks = [];
        mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
        mr.onstop = () => {
          const tipoFinal = mr.mimeType || mimeType || 'audio/ogg';
          this.audioBlob = new Blob(chunks, { type: tipoFinal });
          this.audioUrl = URL.createObjectURL(this.audioBlob);
          stream.getTracks().forEach((t) => t.stop());
          // eslint-disable-next-line no-console
          console.log('[Audio] gravação parada. blob:', { type: this.audioBlob.type, size: this.audioBlob.size });
          // Se o usuário clicou no botão "enviar" durante a gravação,
          // dispara o envio agora que o blob existe.
          if (this.enviarAposStop) {
            this.enviarAposStop = false;
            this.enviarAudio();
          }
        };
        mr.start();
        this.mediaRecorder = mr;
        this.gravando = true;
        this.tempoGravacao = 0;
        this.timerGravacao = setInterval(() => { this.tempoGravacao += 1; }, 1000);
        // eslint-disable-next-line no-console
        console.log('[Audio] gravação iniciada com mr.mimeType:', mr.mimeType);
      } catch (error) {
        // eslint-disable-next-line no-alert
        alert(`Não foi possível acessar o microfone: ${error.message}`);
      }
    },
    pararGravacao() {
      if (this.mediaRecorder && this.gravando) {
        this.mediaRecorder.stop();
      }
      this.gravando = false;
      this.pausado = false;
      clearInterval(this.timerGravacao);
    },
    togglePausar() {
      if (!this.mediaRecorder || !this.gravando) return;
      if (this.pausado) {
        try { this.mediaRecorder.resume(); } catch (_e) { /* noop */ }
        this.pausado = false;
        this.timerGravacao = setInterval(() => { this.tempoGravacao += 1; }, 1000);
      } else {
        try { this.mediaRecorder.pause(); } catch (_e) { /* noop */ }
        this.pausado = true;
        clearInterval(this.timerGravacao);
      }
    },
    descartarGravacao() {
      // Para o gravador (se ainda ativo) e descarta tudo, voltando ao composer.
      const mr = this.mediaRecorder;
      if (mr && (this.gravando || this.pausado)) {
        this.enviarAposStop = false;
        // Substitui o onstop ANTES de parar — o handler original cria
        // um Blob a partir dos chunks (assíncrono) e reabriria o preview
        // mesmo após `_limparGravacao` zerar tudo. Aqui só fechamos o stream.
        mr.onstop = () => {
          try { mr.stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
        };
        try { mr.stop(); } catch (_e) { /* noop */ }
      }
      this._limparGravacao();
    },
    finalizarEEnviarAudio() {
      // Durante gravação: para o gravador e o onstop dispara o envio
      // assim que o blob fica pronto. Já com blob pronto: envia direto.
      if (this.gravando || this.pausado) {
        this.enviarAposStop = true;
        this.pararGravacao();
      } else if (this.audioBlob) {
        this.enviarAudio();
      }
    },
    descartarAudio() {
      this._limparGravacao();
    },
    _limparGravacao() {
      this.gravando = false;
      this.pausado = false;
      this.enviarAposStop = false;
      clearInterval(this.timerGravacao);
      if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
      this.audioBlob = null;
      this.audioUrl = null;
      this.tempoGravacao = 0;
      if (this.mediaRecorder) {
        try { this.mediaRecorder.stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
        this.mediaRecorder = null;
      }
    },
    async enviarAudio() {
      if (!this.audioBlob || !this.conversaId) return;
      this.enviando = true;
      try {
        const tipoBlob = (this.audioBlob.type || 'audio/ogg').split(';')[0];
        const ext = tipoBlob.includes('ogg') ? 'ogg'
          : tipoBlob.includes('mp4') ? 'm4a'
            : tipoBlob.includes('mpeg') ? 'mp3'
              : tipoBlob.includes('webm') ? 'webm'
                : 'ogg';
        const filename = `audio-${Date.now()}.${ext}`;
        const form = new FormData();
        form.append('file', this.audioBlob, filename);
        form.append('tipo', 'audio');
        // eslint-disable-next-line no-console
        console.log('[Audio] enviando:', { tipoBlob, ext, filename, size: this.audioBlob.size });
        await axios.post(`/conversa/${this.conversaId}/mensagem/audio`, form);
        this._limparGravacao();
      } catch (error) {
        const data = error.response && error.response.data;
        const msg = (data && (data.details || data.error)) || error.message;
        // eslint-disable-next-line no-console
        console.error('[Audio] falha completa:', { status: error.response && error.response.status, data, message: error.message });
        // eslint-disable-next-line no-alert
        alert(`Falha ao enviar áudio: ${msg}`);
      } finally {
        this.enviando = false;
      }
    },

    // ── Localização ────────────────────────────────────────────────────
    async enviarLocalizacao() {
      if (!this.conversaId) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm('Compartilhar sua localização atual com o filiado?')) return;
      this.enviandoLocalizacao = true;
      try {
        const pos = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) return reject(new Error('Geolocalização não suportada.'));
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
          return null;
        });
        await axios.post(`/conversa/${this.conversaId}/mensagem/localizacao`, {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.error) || error.message;
        // eslint-disable-next-line no-alert
        alert(`Falha ao enviar localização: ${msg}`);
      } finally {
        this.enviandoLocalizacao = false;
      }
    },
  },
};
</script>

<template>
  <div class="composer-wrapper border-top">
    <!-- Banner de resposta -->
    <div v-if="previaResposta" class="reply-banner d-flex align-items-stretch border-bottom">
      <div class="reply-bar"></div>
      <div class="flex-grow-1 px-2 py-1" style="min-width: 0;">
        <div class="reply-autor">
          <i class="ri-reply-line me-1"></i>Respondendo a <strong>{{ autorResposta }}</strong>
        </div>
        <div class="reply-texto text-truncate">{{ previaResposta }}</div>
      </div>
      <button type="button" class="btn btn-link btn-sm reply-fechar" title="Cancelar resposta" @click="cancelarResposta">
        <i class="ri-close-line"></i>
      </button>
    </div>

    <!-- Preview de arquivo selecionado -->
    <div v-if="arquivoSelecionado" class="preview-anexo p-2 border-bottom">
      <div class="d-flex align-items-start gap-2">
        <img v-if="tipoSelecionado === 'imagem'" :src="previewArquivo" class="preview-img" alt="preview" />
        <video v-else-if="tipoSelecionado === 'video'" :src="previewArquivo" class="preview-img" controls></video>
        <div v-else class="preview-doc">
          <i class="ri-file-line" style="font-size: 2rem;"></i>
          <div class="small text-truncate">{{ arquivoSelecionado.name }}</div>
        </div>
        <div class="flex-grow-1">
          <input
            v-model="caption"
            type="text"
            class="form-control form-control-sm"
            placeholder="Legenda (opcional)"
            @keydown.enter="enviarArquivo"
          />
          <div class="d-flex gap-1 mt-1">
            <button type="button" class="btn btn-success btn-sm" :disabled="enviando" @click="enviarArquivo">
              <i class="ri-send-plane-2-line me-1"></i>Enviar
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="cancelarArquivo">Cancelar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Emoji picker -->
    <div v-if="mostrarEmoji" ref="emojiBox" class="emoji-picker">
      <button
        v-for="(e, i) in emojis"
        :key="i"
        type="button"
        class="emoji-btn"
        @click="inserirEmoji(e)"
      >{{ e }}</button>
    </div>

    <!-- Prévia de link ao vivo (igual WhatsApp Web) -->
    <div
      v-if="!emModoGravacao && !arquivoSelecionado && (linkPreviewLoading || linkPreview)"
      class="composer-linkprev"
    >
      <div v-if="linkPreviewLoading" class="composer-linkprev-load">
        <i class="ri-loader-4-line spin"></i>
        <span>Carregando prévia do link…</span>
      </div>
      <template v-else>
        <img
          v-if="linkPreview.image"
          :src="resolveAssetUrl(linkPreview.image)"
          class="composer-linkprev-thumb"
          alt=""
          @error="$event.target.remove()"
        />
        <div class="composer-linkprev-body">
          <div v-if="linkPreview.title" class="composer-linkprev-title text-truncate">{{ linkPreview.title }}</div>
          <div v-if="linkPreview.description" class="composer-linkprev-desc">{{ linkPreview.description }}</div>
          <div class="composer-linkprev-url text-truncate">{{ linkPreview.siteName || linkPreview.url }}</div>
        </div>
      </template>
      <button type="button" class="composer-linkprev-close" title="Remover prévia" @click="fecharPreviewLink">
        <i class="ri-close-line"></i>
      </button>
    </div>

    <!-- Composer principal — linha única estilo WhatsApp (oculto durante gravação) -->
    <div v-if="!emModoGravacao" class="composer composer-wa">
      <!-- Botão "+" com menu popup (anexos / formatação) -->
      <div class="composer-action-wrapper">
        <button
          type="button"
          class="composer-action-btn"
          :disabled="disabled"
          title="Anexar / formatar"
          @click.stop="mostrarMenuMais = !mostrarMenuMais"
        >
          <i :class="mostrarMenuMais ? 'ri-close-line' : 'ri-add-line'"></i>
        </button>
        <div v-if="mostrarMenuMais" ref="menuMaisBox" class="composer-popup composer-popup--menu" @click.stop>
          <button type="button" class="popup-item" @click="abrirAnexoArquivo">
            <span class="popup-ico" style="color:#7c5cff"><i class="ri-file-text-line"></i></span>
            <span>Documento</span>
          </button>
          <button type="button" class="popup-item" @click="abrirAnexoImagem">
            <span class="popup-ico" style="color:#1fa3ff"><i class="ri-image-2-line"></i></span>
            <span>Foto / Vídeo</span>
          </button>
          <button type="button" class="popup-item" :disabled="enviandoLocalizacao" @click="chamarLocalizacao">
            <span class="popup-ico" style="color:#12b76a"><i class="ri-map-pin-line"></i></span>
            <span>Localização</span>
          </button>
          <div class="popup-divisor"></div>
          <div class="popup-formatos">
            <button type="button" class="popup-formato" title="Negrito (*texto*)" @click="aplicarFormatacao('*', '*')">
              <i class="ri-bold"></i>
            </button>
            <button type="button" class="popup-formato" title="Itálico (_texto_)" @click="aplicarFormatacao('_', '_')">
              <i class="ri-italic"></i>
            </button>
            <button type="button" class="popup-formato" title="Tachado (~texto~)" @click="aplicarFormatacao('~', '~')">
              <i class="ri-strikethrough"></i>
            </button>
            <button type="button" class="popup-formato" title="Monoespaçado (```texto```)" @click="aplicarFormatacao('```', '```')">
              <i class="ri-code-line"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Botão emoji -->
      <button
        type="button"
        class="composer-action-btn"
        :disabled="disabled"
        title="Emoji"
        @click.stop="mostrarEmoji = !mostrarEmoji"
      >
        <i class="ri-emotion-happy-line"></i>
      </button>

      <!-- Inputs ocultos (acionados pelo menu "+") -->
      <input ref="inputArquivo" type="file" class="d-none" @change="onArquivoEscolhido" />
      <input ref="inputImagem" type="file" accept="image/*,video/*" capture="environment" class="d-none" @change="onArquivoEscolhido" />

      <!-- Campo de texto -->
      <textarea
        ref="textarea"
        v-model="texto"
        class="composer-input"
        rows="1"
        placeholder="Digite uma mensagem..."
        :disabled="disabled || enviando"
        @keydown="onKeyDown"
      ></textarea>

      <!-- Botão duplo: sem texto → microfone, com texto → enviar -->
      <button
        v-if="mostrarBotaoMicrofone"
        type="button"
        class="btn composer-send-btn composer-mic-btn"
        :disabled="disabled || enviando"
        title="Gravar áudio"
        @click="iniciarGravacao"
      >
        <i class="ri-mic-line"></i>
      </button>
      <button
        v-else
        type="button"
        class="btn composer-send-btn"
        :disabled="!podeEnviarTexto"
        title="Enviar mensagem"
        @click="enviarTexto"
      >
        <i v-if="enviando" class="ri-loader-4-line spin"></i>
        <i v-else class="ri-send-plane-2-line"></i>
      </button>
    </div>

    <!-- Barra de gravação (substitui toolbar + composer) — UX igual WhatsApp Web -->
    <div v-else class="recording-bar">
      <button
        type="button"
        class="recording-btn recording-btn--ghost"
        title="Descartar áudio"
        :disabled="enviando"
        @click="descartarGravacao"
      >
        <i class="ri-delete-bin-line"></i>
      </button>

      <div class="recording-meta">
        <span class="rec-dot" :class="{ 'rec-dot--pausado': pausado || !gravando }"></span>
        <span class="recording-time">{{ duracaoFormatada }}</span>
      </div>

      <div
        class="recording-waveform"
        :class="{ 'recording-waveform--ativo': gravando && !pausado }"
        aria-hidden="true"
      >
        <span
          v-for="n in 36"
          :key="n"
          :style="{ animationDelay: `${(n % 6) * 0.1}s` }"
        ></span>
      </div>

      <button
        v-if="gravando"
        type="button"
        class="recording-btn recording-btn--ghost"
        :title="pausado ? 'Retomar gravação' : 'Pausar gravação'"
        @click="togglePausar"
      >
        <i :class="pausado ? 'ri-play-fill' : 'ri-pause-fill'"></i>
      </button>

      <button
        type="button"
        class="recording-btn recording-btn--send"
        :disabled="enviando"
        title="Enviar áudio"
        @click="finalizarEEnviarAudio"
      >
        <i v-if="enviando" class="ri-loader-4-line spin"></i>
        <i v-else class="ri-send-plane-2-line"></i>
      </button>
    </div>
  </div>
</template>

<style scoped>
.composer-wrapper {
  background: var(--vz-body-bg);
  border-color: var(--vz-border-color);
}

/* Banner de resposta */
.reply-banner {
  background: var(--vz-tertiary-bg);
}
.reply-bar {
  width: 4px;
  background: var(--vz-primary);
  flex-shrink: 0;
}
.reply-autor {
  font-size: 0.7rem;
  color: var(--vz-primary);
  font-weight: 500;
}
.reply-texto {
  font-size: 0.8rem;
  color: var(--vz-secondary-color);
}
.reply-fechar {
  color: var(--vz-secondary-color);
  padding: 0 0.6rem;
}
.reply-fechar:hover { color: var(--vz-danger); }

.composer { background: var(--vz-body-bg); }

/* --- Barra unificada estilo WhatsApp --------------------------------- */
.composer-wa {
  display: flex;
  align-items: flex-end;
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  background: var(--vz-body-bg);
}
.composer-action-wrapper {
  position: relative;
  display: inline-flex;
}
.composer-action-btn {
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  color: var(--vz-secondary-color);
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
  padding: 0;
}
.composer-action-btn:hover:not(:disabled) {
  background: var(--vz-tertiary-bg);
  color: var(--vz-primary);
}
.composer-action-btn:disabled { opacity: 0.45; cursor: not-allowed; }

/* Input ovalado — encosta nos botões laterais com ar igual ao WhatsApp */
.composer-input {
  flex: 1;
  min-width: 0;
  resize: none;
  max-height: 120px;
  border: 1px solid var(--vz-border-color);
  border-radius: 22px;
  padding: 0.55rem 1rem;
  font-size: 0.9rem;
  line-height: 1.4;
  background: #fff;
  color: var(--vz-body-color);
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.composer-input:focus {
  border-color: #c9ced4;
  box-shadow: none;
}
.composer-input:disabled {
  background: var(--vz-tertiary-bg);
  color: var(--vz-secondary-color);
  cursor: not-allowed;
}

/* Popup do botão "+" (anexos + formatação) */
.composer-popup {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  background: var(--vz-body-bg);
  border: 1px solid var(--vz-border-color);
  border-radius: 0.6rem;
  padding: 0.35rem;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  z-index: 6;
  min-width: 200px;
}
.popup-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  border: 0;
  background: transparent;
  color: var(--vz-body-color);
  padding: 0.5rem 0.7rem;
  border-radius: 0.4rem;
  cursor: pointer;
  font-size: 0.875rem;
  text-align: left;
}
.popup-item:hover:not(:disabled) { background: var(--vz-tertiary-bg); }
.popup-item:disabled { opacity: 0.55; cursor: not-allowed; }
.popup-item-icon {
  font-size: 1.1rem;
  color: var(--vz-primary);
  width: 1.25rem;
  text-align: center;
}
/* Ícone colorido com círculo tint (color-mix usa a cor inline do próprio ícone),
   igual ao menu "+" dos grupos. */
.popup-ico {
  width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center; font-size: 1.2rem;
  background: color-mix(in srgb, currentColor 16%, transparent);
}
.popup-divisor {
  height: 1px;
  background: var(--vz-border-color);
  margin: 0.3rem 0.2rem;
}
.popup-formatos {
  display: flex;
  gap: 2px;
  padding: 0.2rem 0.3rem;
}
.popup-formato {
  flex: 1;
  border: 0;
  background: transparent;
  color: var(--vz-secondary-color);
  font-size: 1rem;
  padding: 0.45rem 0;
  border-radius: 0.35rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.popup-formato:hover { background: var(--vz-tertiary-bg); color: var(--vz-primary); }

.composer-send-btn {
  background: var(--vz-primary);
  border-color: var(--vz-primary);
  color: #fff;
  height: 40px;
  width: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  /* Centraliza o ícone — sem isso o padding default do .btn desloca. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 1.05rem;
  line-height: 1;
}
.composer-send-btn i { line-height: 1; }
.composer-send-btn:hover:not(:disabled) {
  background: var(--brand-primary-hover, #7a0e14);
  color: #fff;
}
.composer-send-btn:disabled { opacity: 0.5; }

/* Prévia de link ao vivo no composer (igual WhatsApp Web) */
.composer-linkprev {
  display: flex; align-items: stretch; gap: 0.6rem;
  margin: 0.5rem 0.75rem 0; padding: 0.5rem;
  background: var(--vz-tertiary-bg);
  border: 1px solid var(--vz-border-color);
  border-left: 3px solid var(--vz-primary);
  border-radius: 0.5rem; position: relative;
}
.composer-linkprev-load {
  display: flex; align-items: center; gap: 0.5rem;
  color: var(--vz-secondary-color); font-size: 0.85rem; padding: 0.25rem 0.15rem;
}
.composer-linkprev-thumb {
  width: 56px; height: 56px; object-fit: cover; border-radius: 0.375rem; flex-shrink: 0;
}
.composer-linkprev-body { min-width: 0; flex: 1; padding-right: 1.25rem; }
.composer-linkprev-title { font-weight: 600; font-size: 0.85rem; color: var(--vz-body-color); }
.composer-linkprev-desc {
  font-size: 0.78rem; color: var(--vz-secondary-color);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.composer-linkprev-url { font-size: 0.72rem; color: var(--vz-secondary-color); text-transform: lowercase; }
.composer-linkprev-close {
  position: absolute; top: 4px; right: 4px;
  width: 24px; height: 24px; border: 0; border-radius: 50%;
  background: transparent; color: var(--vz-secondary-color);
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer; font-size: 1rem;
}
.composer-linkprev-close:hover { background: var(--vz-border-color); color: var(--vz-body-color); }

/* Preview anexo */
.preview-anexo { background: var(--vz-tertiary-bg); }
.preview-img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 0.375rem;
  flex-shrink: 0;
}
.preview-doc {
  width: 80px;
  height: 80px;
  background: var(--vz-body-bg);
  border: 1px solid var(--vz-border-color);
  border-radius: 0.375rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0.25rem;
}
.preview-doc .small {
  width: 100%;
  text-align: center;
  font-size: 0.65rem;
}

/* --- Barra de gravação (estilo WhatsApp Web) ---------------------- */
.recording-bar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.75rem;
  background: var(--vz-body-bg);
  min-height: 56px;
}
.recording-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}
.recording-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.recording-btn--ghost {
  background: transparent;
  color: var(--vz-secondary-color);
}
.recording-btn--ghost:hover:not(:disabled) {
  background: var(--vz-tertiary-bg);
  color: var(--vz-body-color);
}
.recording-btn--send {
  background: var(--vz-primary);
  color: #fff;
}
.recording-btn--send:hover:not(:disabled) { filter: brightness(0.92); }

.recording-meta {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  min-width: 64px;
}
.recording-time {
  font-size: 0.875rem;
  color: var(--vz-body-color);
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}
.rec-dot {
  width: 10px;
  height: 10px;
  background: #dc3545;
  border-radius: 50%;
  animation: rec-pulse 1.2s ease-in-out infinite;
  flex-shrink: 0;
}
.rec-dot--pausado {
  animation: none;
  opacity: 0.5;
}
@keyframes rec-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(0.85); }
}

/* Waveform: barrinhas verticais animadas, igual o efeito do WhatsApp.
   Cor primary do projeto enquanto ativo; cinza quando pausado. */
.recording-waveform {
  display: flex;
  align-items: center;
  flex-grow: 1;
  height: 28px;
  gap: 3px;
  overflow: hidden;
  min-width: 0;
}
.recording-waveform span {
  display: block;
  width: 2px;
  height: 4px;
  background: var(--vz-secondary-color);
  border-radius: 2px;
  flex-shrink: 0;
  opacity: 0.55;
}
.recording-waveform--ativo span {
  background: var(--vz-primary);
  opacity: 1;
  animation: rec-wave 1.1s ease-in-out infinite;
}
@keyframes rec-wave {
  0%, 100% { height: 4px; }
  20%      { height: 14px; }
  40%      { height: 22px; }
  60%      { height: 10px; }
  80%      { height: 18px; }
}

/* Emoji picker — flutua acima da barra do composer */
.composer-wrapper { position: relative; padding-bottom: env(safe-area-inset-bottom, 0px); }
.emoji-picker {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0.5rem;
  background: var(--vz-body-bg);
  border: 1px solid var(--vz-border-color);
  border-radius: 0.5rem;
  padding: 0.5rem;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 0.15rem;
  max-width: 280px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 5;
}
.emoji-btn {
  background: transparent;
  border: 0;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.2rem;
  border-radius: 0.25rem;
  transition: background 0.1s;
}
.emoji-btn:hover { background: var(--vz-tertiary-bg); }

.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* --- Dark mode -------------------------------------------------------- */
[data-bs-theme="dark"] .composer-wrapper,
[data-bs-theme="dark"] .composer,
[data-bs-theme="dark"] .composer-wa {
  background: #14181d;
}
[data-bs-theme="dark"] .composer-input {
  background: #2a3038;
  border-color: #3a4049;
  color: #e6e8eb;
}
[data-bs-theme="dark"] .composer-input:focus {
  border-color: #4a525c;
  box-shadow: none;
}
[data-bs-theme="dark"] .composer-input:disabled {
  background: #181c22;
  color: #7a8089;
}
[data-bs-theme="dark"] .composer-action-btn {
  color: #b8bdc4;
}
[data-bs-theme="dark"] .composer-action-btn:hover:not(:disabled) {
  background: #1f252c;
  color: #ff9aa5;
}
[data-bs-theme="dark"] .composer-popup {
  background: #1c2128;
  border-color: #2a3038;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.5);
}
[data-bs-theme="dark"] .popup-item {
  color: #e6e8eb;
}
[data-bs-theme="dark"] .popup-item:hover:not(:disabled) {
  background: #262c34;
}
[data-bs-theme="dark"] .popup-divisor {
  background: #2a3038;
}
[data-bs-theme="dark"] .popup-formato {
  color: #b8bdc4;
}
[data-bs-theme="dark"] .popup-formato:hover {
  background: #262c34;
  color: #ff9aa5;
}
[data-bs-theme="dark"] .emoji-picker {
  background: #1c2128;
  border-color: #2a3038;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}
[data-bs-theme="dark"] .emoji-btn:hover {
  background: #262c34;
}
[data-bs-theme="dark"] .reply-banner {
  background: #1c2128;
}
[data-bs-theme="dark"] .preview-anexo {
  background: #1c2128;
}
[data-bs-theme="dark"] .recording-bar {
  background: #14181d;
}
[data-bs-theme="dark"] .composer-linkprev {
  background: #1c2128;
  border-color: #2a3038;
}
[data-bs-theme="dark"] .composer-linkprev-close:hover {
  background: #2a3038;
  color: #e6e8eb;
}
</style>
